import {
  NgSignaturePadOptions,
  SignaturePadComponent,
} from '@almothafar/angular-signature-pad';
import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ViewChild,
  Inject,
  HostListener,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EventTypes } from 'src/app/core/models/events.model';
import { EventService } from 'src/app/core/services/event.service';

@Component({
  selector: 'app-signature-pad-electronic-assignature',
  templateUrl: './signature-pad-electronic-assignature.component.html',
  styleUrls: ['./signature-pad-electronic-assignature.component.scss'],
})
export class SignaturePadElectronicAssignatureComponent
  implements AfterViewInit, OnChanges, OnInit
{
  @Output() backEventEmitter = new EventEmitter();
  @Output() signEventEmitter = new EventEmitter();
  @Output() termsEventEmiiter = new EventEmitter();
  data: any;

  @ViewChild('signature', { static: true })
  public signaturePad!: SignaturePadComponent;

  // External state controlled by the parent (modal)
  @Input() signLoading: boolean = false;
  @Input() signSuccess: boolean = false;
  @Input() signError: string | null = null;
  signaturePadOptions: NgSignaturePadOptions = {
    canvasHeight: 300,
    canvasWidth: 500,
    minWidth: 5,
  };
  checkbox = false;
  disabled = true;
  loading = false;
  isGuideRoute: boolean;
  inkGuardError: string | null = null;

  // Threshold from the CPK-4349 signature forensics study (18/08/2026):
  // strokes narrower than this are blank canvases or single-tap artifacts,
  // not signatures — matches the same guard enforced server-side.
  private static readonly MIN_STROKE_WIDTH_PX = 15;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute
  ) {
    this.isGuideRoute = this.router.url.includes('guide');
  }

  async ngOnInit(): Promise<any> {
    await this.sendCanvaSignatureEvent();
  }

  ngAfterViewInit(): void {
    this.signaturePad.set(
      'canvasWidth',
      this.document.getElementById('content-signature')?.clientWidth
    );
    this.signaturePad.set(
      'canvasHeight',
      this.document.getElementById('content-signature')?.clientHeight
    );

    (
      this.document.getElementById('signature-component') as HTMLElement
    ).style.display = 'flex';
    (
      this.document.getElementById('signature-component') as HTMLElement
    ).style.justifyContent = 'center';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['signLoading'] && !changes['signLoading'].isFirstChange()) {
      this.loading = !!changes['signLoading'].currentValue;
    }

    if (changes['signSuccess'] && changes['signSuccess'].currentValue) {
      this.loading = false;
      this.disabled = true;
    }

    if (changes['signError'] && changes['signError'].currentValue) {
      this.loading = false;
    }
  }

  drawComplete(event: MouseEvent | Touch) {
    const result = this.cropToInkBounds(this.signaturePad);

    if (!result.valid) {
      this.data = null;
      this.disabled = true;
      this.inkGuardError =
        result.reason === 'no_ink'
          ? 'SignatureBlankTryAgain'
          : 'SignatureTooSmallTryAgain';
      return;
    }

    this.data = result.dataUrl;
    this.disabled = false;
    this.inkGuardError = null;
  }

  drawStart(event: MouseEvent | Touch) {}

  drawClear() {
    this.signaturePad.clear();
    this.data = null;
    this.inkGuardError = null;
  }

  doSignDocument() {
    if (!this.data || this.loading) return;
    this.loading = true;
    this.signEventEmitter.emit(this.data);
  }

  private cropToInkBounds(pad: SignaturePadComponent): {
    valid: boolean;
    dataUrl: string;
    reason?: 'no_ink' | 'stroke_too_narrow';
  } {
    const points = pad.toData();

    if (!points || points.length === 0) {
      return { valid: false, dataUrl: pad.toDataURL(), reason: 'no_ink' };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const group of points) {
      for (const point of (group as any).points ?? group) {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.y > maxY) maxY = point.y;
      }
    }

    if (!isFinite(minX)) {
      return { valid: false, dataUrl: pad.toDataURL(), reason: 'no_ink' };
    }

    if (maxX - minX < SignaturePadElectronicAssignatureComponent.MIN_STROKE_WIDTH_PX) {
      return { valid: false, dataUrl: pad.toDataURL(), reason: 'stroke_too_narrow' };
    }

    const padding = 16;
    const srcCanvas = pad.getCanvas();
    const srcX = Math.max(0, minX - padding);
    const srcY = Math.max(0, minY - padding);
    const srcW = Math.min(srcCanvas.width - srcX, maxX - minX + padding * 2);
    const srcH = Math.min(srcCanvas.height - srcY, maxY - minY + padding * 2);

    const trimCanvas = this.document.createElement('canvas');
    trimCanvas.width = srcW;
    trimCanvas.height = srcH;
    const ctx = trimCanvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, srcW, srcH);
    ctx.drawImage(srcCanvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

    return { valid: true, dataUrl: trimCanvas.toDataURL('image/png') };
  }

  private async sendCanvaSignatureEvent(): Promise<any> {
    const claimIdUrlParam = this.activatedRoute.snapshot.paramMap.get('claimId');
    const documentEvent: EventTypes = EventTypes.SIGNATURE_CANVA_LOAD_PAGE;

    try {
      await this.eventService.sendEventWithCaseId(documentEvent, claimIdUrlParam);
    } catch (error) {
      console.error(error);
    }
  }
}
